import { useState } from "react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { useEntitlement } from "../lib/useEntitlement";
import { useUnits } from "../lib/useUnits";
import { detailAllowance } from "../lib/detailGate";
import { SheetShell } from "./SheetShell";
import { Button } from "./ui/Button";
import { WeightStepper } from "./WeightStepper";
import { hapticSuccess } from "../lib/haptics";
import { recomputeMacros, formatEntryName, normalizeCode } from "../lib/barcode";
import type { MealSlot, NormalizedProduct, ServingUnit } from "@shared/types";

interface Props {
  product: NormalizedProduct;
  slot: MealSlot;
  eatenOn: string;
  onAdded: () => void | Promise<void>;
  onClose: () => void;
}

/** Amount + confirm step for an OFF search result. Logs to the given meal and
 *  best-effort records/caches the barcode so a future scan resolves instantly. */
export function QuantitySheet({ product, slot, eatenOn, onAdded, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const [amount, setAmount] = useState(
    product.serving_size_g && product.serving_size_g > 0 ? product.serving_size_g : 100,
  );
  const [unit, setUnit] = useState<ServingUnit>(product.unit ?? "g");
  const [busy, setBusy] = useState(false);
  const { premium } = useEntitlement();
  const units = useUnits();

  const amt = Math.round(amount);
  const valid = Number.isFinite(amt) && amt >= 1 && amt <= 2000;
  const macros = valid ? recomputeMacros(product.per_100g, amt) : null;

  async function add() {
    if (!valid || !macros) return;
    setBusy(true);
    try {
      await api.addFood({
        eaten_on: eatenOn, source: "custom", plan_meal_slot: slot,
        name: formatEntryName(product),
        calories: macros.calories,
        // Detail amounts are Pro — a free add logs the bare entry.
        ...detailAllowance(premium, {
          protein_g: macros.protein_g, carbs_g: macros.carbs_g, fat_g: macros.fat_g,
          sugar_g: macros.sugar_g, fiber_g: macros.fiber_g, micros: macros.micros,
        }),
      });
      const code = normalizeCode(product.code);
      if (code) {
        api.barcodeRecordRecent(code).catch(() => { /* best-effort */ });
        api.barcodeCachePut(code, product).catch(() => { /* best-effort */ });
      }
      await onAdded();
      hapticSuccess();
      toast.success(t("toast.logged"));
      onClose();
    } catch {
      toast.error(t("toast.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <SheetShell onClose={onClose}>
      <h3 className="text-lg font-semibold mt-4 mb-1 truncate">{formatEntryName(product)}</h3>
      <p className="text-sm text-fg-muted mb-4 tabular-nums">{units.energyStr(product.per_100g.kcal)} / 100 {unit}</p>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-sm text-fg-muted">{t("quantity.amount")}</span>
        <WeightStepper
          value={amount}
          unit={unit}
          onChange={(a, u) => { setAmount(a); setUnit(u); }}
          max={2000}
          aria-label={t("quantity.amount")}
        />
        <span className="text-sm text-fg-muted tabular-nums">{macros ? units.energyStr(macros.calories) : "—"}</span>
      </div>
      <Button onClick={add} disabled={!valid || busy} className="w-full">
        {busy ? t("food_sheet.saving") : t("quantity.add")}
      </Button>
    </SheetShell>
  );
}
